import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './ForgotPassword.css';
import BaseUrl from '../../ApiServices/BaseUrl';
import Navbar from "../../Pages/Navbar/Navbar";
import { FaEye, FaEyeSlash } from 'react-icons/fa';

function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
    const [otp, setOtp] = useState(['', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [uid, setUid] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [timer, setTimer] = useState(0);
    const [canResendOtp, setCanResendOtp] = useState(true);
    const navigate = useNavigate();

    // Handle OTP input
    const handleOtpChange = (index, value) => {
        if (value.length > 1) return;
        
        const newOtp = [...otp];
        newOtp[index] = value.replace(/\D/g, '');
        
        if (value && index < 3) {
            document.getElementById(`otp-${index + 1}`).focus();
        }
        
        setOtp(newOtp);
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            document.getElementById(`otp-${index - 1}`).focus();
        }
    };

    // Start OTP timer
    const startOtpTimer = () => {
        setTimer(60);
        setCanResendOtp(false);
        
        const interval = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setCanResendOtp(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Send OTP to email for forgot password
    const sendForgotOtp = async (targetEmail = email) => {
        try {
            const response = await fetch(`${BaseUrl}/send-forgot-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: targetEmail })
            });

            const data = await response.json();
            
            if (data.success) {
                startOtpTimer();
                setSuccess('OTP sent to your email!');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                throw new Error(data.message || 'Failed to send OTP');
            }
        } catch (err) {
            console.error('Failed to send OTP:', err);
            setError(err.message || 'Failed to send OTP. Please try again.');
        }
    };

    const handleEmailSubmit = async () => {
        const normalizedEmail = email.trim();
        if (!normalizedEmail) {
            setError('Please enter your email address');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Verify if email exists and get user ID
            const res = await fetch(`${BaseUrl}/users/get-userId?email=${encodeURIComponent(normalizedEmail)}`);
            const data = await res.json();
            
            if (data.success) {
                setEmail(normalizedEmail);
                setUid(data.id);
                // Send OTP for forgot password
                await sendForgotOtp(normalizedEmail);
                setStep(2);
            } else {
                setError('Email not found. Please check your email address.');
            }
        } catch (err) {
            console.error('Failed to verify email:', err);
            setError('Something went wrong while verifying email.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpSubmit = async () => {
        const enteredOtp = otp.join('');
        
        if (enteredOtp.length !== 4) {
            setError('Please enter the complete 4-digit OTP');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Verify OTP using forgot password endpoint
            const response = await fetch(`${BaseUrl}/verify-forgot-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, otp: enteredOtp })
            });

            const data = await response.json();
            
            if (data.success) {
                setStep(3);
                setSuccess('OTP verified successfully!');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(data.message || 'Invalid OTP. Please try again.');
            }
        } catch (err) {
            console.error('Failed to verify OTP:', err);
            setError('Something went wrong while verifying OTP.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (!canResendOtp) return;
        
        setIsLoading(true);
        setError('');
        
        try {
            await sendForgotOtp();
            setSuccess('New OTP sent!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to resend OTP. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        if (!newPassword || !confirmPassword) {
            setError('Please fill in all password fields');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords don't match");
            return;
        }

        if (newPassword.length < 6) {
            setError('Password should be at least 6 characters long');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(`${BaseUrl}/api/users/${uid}/password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newPassword, email })
            });

            const data = await response.json();
            
            if (data.success) {
                setSuccess('Password updated successfully! Redirecting to login...');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                throw new Error(data.message || 'Failed to update password');
            }
        } catch (err) {
            console.error('Failed to update password:', err);
            setError(err.message || 'Something went wrong while resetting password.');
        } finally {
            setIsLoading(false);
        }
    };

    const goBackToEmail = () => {
        setStep(1);
        setOtp(['', '', '', '']);
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setTimer(0);
        setCanResendOtp(true);
    };

    const goBackToOtp = () => {
        setStep(2);
        setNewPassword('');
        setConfirmPassword('');
        setError('');
    };

    return (
        <>
            <Navbar />
            <div className="forgot-wrapper">
                <div className="forgot-box">
                    <h2>Forgot Password</h2>
                    
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}
                    
                    {success && (
                        <div className="success-message">
                            {success}
                        </div>
                    )}

                    {step === 1 && (
                        <>
                            <div className="forgot-form">
                                <p className="instruction-text">
                                    Enter your registered email address to reset your password.
                                </p>
                                <input
                                    type="email"
                                    placeholder="Enter your registered email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="forgot-input"
                                />
                                <button 
                                    onClick={handleEmailSubmit} 
                                    className="forgot-btn"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Sending OTP...' : 'Send OTP'}
                                </button>
                                <div className="back-to-login">
                                    <Link to="/login">Back to Login</Link>
                                </div>
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <div className="forgot-form">
                                <p className="instruction-text">
                                    Enter the 4-digit OTP sent to <strong>{email}</strong>
                                </p>
                                
                                <div className="otp-container">
                                    {[0, 1, 2, 3].map((index) => (
                                        <input
                                            key={index}
                                            id={`otp-${index}`}
                                            type="text"
                                            maxLength="1"
                                            value={otp[index]}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            className="otp-input"
                                            autoFocus={index === 0}
                                        />
                                    ))}
                                </div>
                                
                                <div className="otp-timer">
                                    {timer > 0 ? (
                                        <span>Resend OTP in {timer}s</span>
                                    ) : (
                                        <button 
                                            type="button" 
                                            onClick={handleResendOtp}
                                            className="resend-otp-btn"
                                            disabled={!canResendOtp || isLoading}
                                        >
                                            Resend OTP
                                        </button>
                                    )}
                                </div>

                                <button 
                                    onClick={handleOtpSubmit} 
                                    className="forgot-btn"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Verifying...' : 'Verify OTP'}
                                </button>
                                
                                <div className="forgot-actions">
                                    <button 
                                        type="button" 
                                        onClick={goBackToEmail}
                                        className="back-button"
                                    >
                                        Back to Email
                                    </button>
                                    <Link to="/login" className="login-link">Back to Login</Link>
                                </div>
                            </div>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <div className="forgot-form">
                                <p className="instruction-text">
                                    Set your new password for <strong>{email}</strong>
                                </p>
                                
                                <div className="password-field">
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        placeholder="New Password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="forgot-input"
                                    />
                                    <span
                                        className="eye-icon"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                    >
                                        {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                    </span>
                                </div>

                                <div className="password-field">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Confirm Password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="forgot-input"
                                    />
                                    <span
                                        className="eye-icon"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                    </span>
                                </div>

                                <button 
                                    onClick={handlePasswordReset} 
                                    className="forgot-btn"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Resetting...' : 'Reset Password'}
                                </button>
                                
                                <div className="forgot-actions">
                                    <button 
                                        type="button" 
                                        onClick={goBackToOtp}
                                        className="back-button"
                                    >
                                        Back to OTP
                                    </button>
                                    <Link to="/login" className="login-link">Back to Login</Link>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default ForgotPassword;
